import { useMemo, useState } from 'react';
import { StrategyBuilder } from 'utils/shared/quantlib/StrategyBuilder';
import { OptionInputParams } from 'utils/shared/quantlib/utils';

export function useStrategyVolatility(
  options: Omit<OptionInputParams, 'spot' | 'riskFreeRate' | 'volatility'>[],
  spot: number,
  riskFreeRate: number,
) {
  const ivSummary = useMemo(
    () =>
      StrategyBuilder.initializedImpliedVolatilities(
        // Constructor placeholder only; never a solved IV or a valuation assumption.
        options.map((option) => ({ ...option, spot, riskFreeRate, volatility: 0.2 })),
      ),
    [options, spot, riskFreeRate],
  );
  const [manual, setManual] = useState<{ snapshot: typeof ivSummary; value: number } | null>(null);
  // A new quote/input snapshot requires a new explicit estimate; no stale fallback on refresh.
  const isManualVolatility = manual?.snapshot === ivSummary;
  const originalStrategyVolatility = ivSummary.averageImpliedVolatility;
  const volatility = isManualVolatility
    ? manual.value
    : originalStrategyVolatility == null
    ? null
    : parseFloat(originalStrategyVolatility.toFixed(3));

  return {
    ivSummary,
    isManualVolatility,
    originalStrategyVolatility,
    volatility,
    setVolatility: (value: number) => {
      if (Number.isFinite(value) && value >= 0.001 && value <= 5)
        setManual({ snapshot: ivSummary, value });
    },
    resetVolatility: () => setManual(null),
  };
}
